/*
 * Isolated pre-order dates. Own GraphQL + sibling JSON.
 * Never writes VariantsPage or [data-dd-variant-data].
 * Fail-open: a 500 here must not empty restock SKUs.
 */
(function () {
  'use strict';

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var CACHE_PREFIX = 'dd_po_v1_';
  var CACHE_TTL = 300000;
  var INPUT_NAME = 'properties[_preorder_promise]';

  /*
   * The buyer-facing twin of the line above, and the only thing that carries
   * the promise past the cart.
   *
   * A leading underscore is what hides a line item property from customer-
   * facing display, which is why `_preorder_promise` was chosen: the theme
   * renders its own styled pre-order row on the PDP and in the cart, so the
   * raw value should not also appear as "key: value" chrome. The cost of that
   * choice is that checkout, the order confirmation email, and the order
   * status page showed the line as an ordinary in-stock item, with whatever
   * delivery estimate the shipping profile produced. Measured 2026-09-10: a
   * pre-order line reached the payment step with no indication it was inbound.
   *
   * Shopify renders NON-underscore properties natively on all of those
   * surfaces, so this second property is what makes the promise visible where
   * we do not control the template. Verified rendering as
   * "Pre-order: Ships by Oct 16" under the line title at checkout
   * (.cursor/scripts/probe-preorder-checkout-line.mjs).
   *
   * Both are kept because they hold different things: the ISO value is what
   * every theme surface parses, and this one is prose that must never be
   * parsed. The wording matches the PDP button exactly (`label()` below and
   * `po_day` in snippets/buy-buttons.liquid) so no two surfaces disagree.
   *
   * The theme's own cart property lists must skip this key or the cart shows
   * the promise twice; see snippets/dd-cart-item.liquid and
   * snippets/dd-cart-item-page.liquid.
   */
  var LABEL_INPUT_NAME = 'properties[Pre-order]';

  /* Create, update, or remove one hidden property input. An empty value means
     remove: an empty `properties[...]` can still land on a native form POST,
     which would put a blank property on the order.

     The input is stamped with the variant it describes so a later pass can tell
     a still-correct value from a stale one without re-deriving the date. */
  function upsertProperty(form, name, value, variantId) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (!value) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    if (variantId != null) input.setAttribute('data-dd-po-variant', String(variantId));
  }

  /* The only removal that is safe while the promise map is unknown: the inputs
     name the variant they belong to, so a mismatch against the current
     selection is positively stale and must not ride along to checkout. A
     missing stamp is treated as current, because Liquid stamps what it
     renders. */
  function dropStaleProperties(form, variantId) {
    if (variantId == null) return;
    var input = form.querySelector('input[name="' + INPUT_NAME + '"]');
    if (!input) return;
    var stamp = input.getAttribute('data-dd-po-variant');
    if (!stamp || stamp === String(variantId)) return;
    upsertProperty(form, INPUT_NAME, '');
    upsertProperty(form, LABEL_INPUT_NAME, '');
  }

  function toIsoLocal(dt) {
    var y = dt.getFullYear();
    var m = String(dt.getMonth() + 1);
    if (m.length < 2) m = '0' + m;
    var d = String(dt.getDate());
    if (d.length < 2) d = '0' + d;
    return y + '-' + m + '-' + d;
  }

  /* The app's PO day as sent, same as dd-restock-label.js. A day already in
     the past steps forward 7 days at a time until it is today or later, and
     stops when the app sends a current date. */
  function customerIso(raw) {
    var date = isoFrom(raw);
    if (!date) return null;
    var p = date.split('-');
    var po = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    if (isNaN(po.getTime())) return null;
    var today = new Date();
    var todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    var guard = 0;
    while (po < todayStart && guard < 520) {
      po = new Date(po.getFullYear(), po.getMonth(), po.getDate() + 7);
      guard += 1;
    }
    return toIsoLocal(po);
  }

  function isoFrom(raw) {
    if (!raw || typeof raw !== 'string') return null;
    var m = /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim());
    return m ? m[1] : null;
  }

  function prettyDay(iso) {
    var date = isoFrom(iso);
    if (!date) return '';
    var p = date.split('-');
    var month = MONTHS[parseInt(p[1], 10) - 1] || '';
    var day = String(parseInt(p[2], 10));
    return month + ' ' + day;
  }

  function numericDay(iso) {
    var date = isoFrom(iso);
    if (!date) return '';
    var p = date.split('-');
    return String(parseInt(p[1], 10)) + '/' + String(parseInt(p[2], 10));
  }

  function byline(iso) {
    var day = prettyDay(iso);
    return day ? 'Ships by ' + day : '';
  }

  function shipNote(iso) {
    var day = numericDay(iso);
    return day
      ? 'Estimated to ship ' + day + '. Subject to delays.'
      : 'Estimated to ship. Subject to delays.';
  }

  function label(iso) {
    var day = prettyDay(iso);
    return day ? 'Pre-order \u00b7 Ships by ' + day : 'Pre-order';
  }

  function atcAddText(host) {
    var root = host && host.closest ? host.closest('[data-add-text]') : null;
    var t = root && root.getAttribute('data-add-text');
    if (t && String(t).trim() && !/^pre-order/i.test(t) && !/^incoming/i.test(t)) {
      return String(t).trim();
    }
    return 'Add to cart';
  }

  function atcFace(iso, addText) {
    var day = numericDay(iso);
    var add = addText && String(addText).trim() ? String(addText).trim() : '';
    var custom =
      add &&
      !/^add to cart$/i.test(add) &&
      !/^pre-order/i.test(add) &&
      !/^incoming/i.test(add);
    if (custom) return day ? add + ' \u00b7 Est. ship ' + day : add;
    return 'Pre-order';
  }

  /*
   * Swatch-card corner badge: "INCOMING 10/14". The eye reads the badge
   * first, so the date goes there; "PRE-ORDER BY 10/14" read as either a
   * cutoff or an opening day. The action lives in the card's hint overlay
   * (incomingHint) as "Pre-order available".
   */
  function incomingFace(iso) {
    var day = numericDay(iso);
    return day ? 'INCOMING ' + day : 'INCOMING';
  }

  function incomingHint() {
    return 'Pre-order available';
  }

  function paintAtcLabel(host, iso) {
    if (!host) return;
    var text = atcFace(iso, atcAddText(host));
    var current = (host.textContent || '').replace(/\s+/g, ' ').trim();
    if (current === text && !host.querySelector('.dd-atc-preorder-title')) return;
    host.classList.remove('dd-atc-button__text--marquee', 'dd-sticky-footer__atc-label--marquee');
    host.style.removeProperty('--dd-atc-marquee-duration');
    host.style.removeProperty('--dd-sf-atc-marquee-duration');
    host.textContent = text;
  }

  function getCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.expires < Date.now() || !parsed.map) return null;
      return parsed.map;
    } catch (e) {
      return null;
    }
  }

  function setCache(key, map) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ expires: Date.now() + CACHE_TTL, map: map }));
    } catch (e) { /* */ }
  }

  var api = {
    map: {},

    /* True once the promises of the product this page sells (pdpHandle) have
       actually been learned: its Storefront fetch resolved, or its Liquid seed
       covers every variant. Until then an absent entry means "unknown", not
       "no promise", and nothing may act on it.

       Cards merge other products into the same map, and the seed stops at 250
       variants. Neither may set this. Measured 2026-10-12: a cart drawer
       element answered the old handle lookup, the page fetched the shipping
       protection product instead of SuperWeft, `loaded` went true, and every
       SuperWeft variant past position 250 sold as in stock. */
    loaded: false,
    pdpHandle: '',
    _pdpSettled: false,
    _inflight: {},
    _known: {},
    _variantInflight: {},

    get: function (id) {
      if (id == null) return null;
      return customerIso(this.map[String(id)]);
    },

    label: label,
    byline: byline,
    shipNote: shipNote,
    prettyDay: prettyDay,
    numericDay: numericDay,
    atcFace: atcFace,
    incomingFace: incomingFace,
    incomingHint: incomingHint,
    paintAtcLabel: paintAtcLabel,

    merge: function (obj) {
      if (!obj || typeof obj !== 'object') return;
      var keys = Object.keys(obj);
      for (var i = 0; i < keys.length; i++) {
        var iso = isoFrom(obj[keys[i]]);
        if (iso) this.map[String(keys[i])] = iso;
      }
    },

    syncForm: function (variantId) {
      var form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      /*
       * Liquid already rendered the promise for the variant the page loaded
       * with, straight from the metafield. This pass may correct that only when
       * it knows MORE than the server did, never when it knows less.
       *
       * `applySurfaces()` re-runs on every `variant:change` and
       * `dd:variants-full`. On a product with no swatch selector there is no
       * current variant and no `__ddVariantLoaderConfig`, so `fetch()` returns
       * an empty map. The old code read that as "no promise" and deleted the
       * server-rendered inputs somewhere between first paint and submit.
       *
       * Measured 2026-09-10 on a single-variant pre-order product: both inputs
       * present in the form's FormData at load, zero `properties[...]` fields
       * in the /cart/add POST body, line stored with no properties at all. That
       * is why no pre-order date had ever reached an order, visible or hidden.
       *
       * Fail-open matches the header of this file: not knowing must change
       * nothing. A promise that IS in the map is knowledge whether or not the
       * rest of the product loaded, so it always rides along.
       */
      if (variantId == null) return;
      var iso = this.get(variantId);
      if (!iso && !this.loaded && !this._known[String(variantId)]) {
        dropStaleProperties(form, variantId);
        return;
      }

      upsertProperty(form, INPUT_NAME, iso || '', variantId);
      /* Human-readable twin of the ISO value. Both are needed and neither is
         redundant: see the comment on LABEL_INPUT_NAME. */
      upsertProperty(form, LABEL_INPUT_NAME, iso ? byline(iso) : '', variantId);
    },

    applySurfaces: function () {
      var ss = document.querySelector('dd-swatch-selector');
      var variant = ss && typeof ss.getCurrentVariant === 'function' ? ss.getCurrentVariant() : null;
      var iso = variant ? this.get(variant.id) : null;

      this.syncForm(variant && variant.id);

      if (typeof window.__ddSyncTrustShip === 'function') {
        try { window.__ddSyncTrustShip(); } catch (e) { /* */ }
      }

      var submit = document.querySelector('.product-form__submit');
      if (submit) submit.classList.toggle('is--preorder-label', !!iso);

      if (ss && typeof ss.applyPreorderBadges === 'function') {
        try { ss.applyPreorderBadges(); } catch (e) { /* */ }
      }

      var sticky = document.querySelector('dd-sticky-footer');
      if (sticky && typeof sticky.updateShippingAndCustom === 'function' && variant) {
        try { sticky.updateShippingAndCustom(variant); } catch (e) { /* */ }
      }
      if (sticky && sticky.button && !sticky.isGuest && typeof sticky.syncButtonFromRenderedHtml === 'function' && variant) {
        try { sticky.syncButtonFromRenderedHtml(null, variant); } catch (e) { /* */ }
      }

      if (variant && !iso && this._pdpSettled && !this.loaded && !this._known[String(variant.id)]) {
        this.lookupVariant(variant.id);
      }
    },

    /* One variant's promise, asked only after the whole-product fetch failed
       and the shopper is on a variant that fetch never answered for. */
    lookupVariant: function (variantId) {
      var self = this;
      var key = String(variantId);
      var cfg = window.__ddVariantLoaderConfig;
      if (!cfg || !cfg.token || this._variantInflight[key]) return;
      this._variantInflight[key] = true;
      fetch('/api/' + (cfg.version || '2026-04') + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token
        },
        body: JSON.stringify({
          query: 'query PreorderVariant($id: ID!) { node(id: $id) { ... on ProductVariant { metafield(namespace: "moqbird", key: "preorder_promise") { value } } } }',
          variables: { id: 'gid://shopify/ProductVariant/' + key }
        })
      }).then(function (r) {
        if (!r.ok) throw new Error('preorder variant ' + r.status);
        return r.json();
      }).then(function (data) {
        if (data && data.errors && data.errors.length) throw new Error('preorder variant graphql');
        var node = data && data.data && data.data.node;
        var iso = isoFrom(node && node.metafield && node.metafield.value);
        if (iso) self.map[key] = iso;
        self._known[key] = true;
        self.applySurfaces();
        try {
          document.dispatchEvent(new CustomEvent('dd:preorder-map-ready'));
        } catch (e) { /* */ }
      }).catch(function (err) {
        console.warn('[dd-preorder]', err && err.message ? err.message : err);
      }).then(function () {
        delete self._variantInflight[key];
      });
    },

    fetch: function (productHandle) {
      var self = this;
      if (!productHandle) return Promise.resolve(this.map);
      var cfg = window.__ddVariantLoaderConfig;
      if (!cfg || !cfg.token) return Promise.resolve(this.map);

      var isPdp = productHandle === this.pdpHandle;
      var cacheKey = CACHE_PREFIX + productHandle;
      var cached = getCache(cacheKey);
      if (cached) {
        this.merge(cached);
        if (isPdp) this.loaded = true;
        return Promise.resolve(this.map);
      }
      if (this._inflight[productHandle]) return this._inflight[productHandle];

      var query = [
        'query PreorderPage($handle: String!, $first: Int!, $after: String) {',
        '  product(handle: $handle) {',
        '    variants(first: $first, after: $after) {',
        '      pageInfo { hasNextPage endCursor }',
        '      nodes {',
        '        id',
        '        metafield(namespace: "moqbird", key: "preorder_promise") { value }',
        '      }',
        '    }',
        '  }',
        '}'
      ].join('\n');

      var collected = {};

      function sfFetch(after) {
        return fetch('/api/' + (cfg.version || '2026-04') + '/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': cfg.token
          },
          body: JSON.stringify({
            query: query,
            variables: { handle: productHandle, first: 250, after: after || null }
          })
        }).then(function (r) {
          if (!r.ok) throw new Error('preorder fetch ' + r.status);
          return r.json();
        }).then(function (data) {
          if (data && data.errors && data.errors.length) throw new Error('preorder graphql');
          var product = data && data.data && data.data.product;
          if (!product) return collected;
          var nodes = product.variants && product.variants.nodes || [];
          var pageInfo = product.variants && product.variants.pageInfo;
          for (var i = 0; i < nodes.length; i++) {
            var n = nodes[i];
            var gid = String(n.id || '');
            var numericId = gid.indexOf('/') !== -1 ? gid.split('/').pop() : gid;
            var raw = n.metafield && n.metafield.value;
            var iso = isoFrom(raw);
            if (iso) collected[numericId] = iso;
          }
          if (pageInfo && pageInfo.hasNextPage && pageInfo.endCursor) {
            return sfFetch(pageInfo.endCursor);
          }
          return collected;
        });
      }

      var pending = sfFetch(null).then(function (map) {
        delete self._inflight[productHandle];
        setCache(cacheKey, map);
        self.merge(map);
        /* A resolved query is knowledge even when it found nothing: it means
           this product has no promises, which is different from not having
           asked. Only the viewed product's answer says anything about the form. */
        if (isPdp) self.loaded = true;
        return self.map;
      }).catch(function (err) {
        delete self._inflight[productHandle];
        if (isPdp) self._pdpSettled = true;
        /* Pages that did answer are still true. Keep them, but the map stays
           not-loaded: a missing variant may sit on the page that failed. */
        self.merge(collected);
        console.warn('[dd-preorder]', err && err.message ? err.message : err);
        return self.map;
      });
      this._inflight[productHandle] = pending;
      return pending;
    }
  };

  window.ddPreorder = api;
  window._ddPreorderMap = api.map;

  function seedSibling() {
    var els = document.querySelectorAll('[data-dd-preorder-data]');
    for (var i = 0; i < els.length; i++) {
      try {
        api.merge(JSON.parse(els[i].textContent));
        if (
          api.pdpHandle &&
          els[i].getAttribute('data-dd-po-handle') === api.pdpHandle &&
          els[i].getAttribute('data-dd-po-complete') === 'true'
        ) {
          api.loaded = true;
        }
      } catch (e) { /* sibling must never break restock */ }
    }
  }

  /* The product this page sells. Never a bare [data-product-handle]: the cart
     drawer's protection widget and every product card carry one, and the
     drawer comes first in the document. */
  function pdpHandle() {
    var el = document.querySelector('dd-swatch-selector[data-product-handle], dd-sticky-footer[data-product-handle]');
    var handle = el ? el.getAttribute('data-product-handle') : '';
    if (!handle) {
      var m = /\/products\/([^/?#]+)/.exec(window.location.pathname || '');
      handle = m ? decodeURIComponent(m[1]) : '';
    }
    return handle;
  }

  function boot() {
    var handle = pdpHandle();
    api.pdpHandle = handle;
    seedSibling();
    var ready = handle ? api.fetch(handle) : Promise.resolve(api.map);
    ready.then(function () {
      api.applySurfaces();
      try {
        document.dispatchEvent(new CustomEvent('dd:preorder-map-ready'));
      } catch (e) { /* */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('variant:change', function () {
    api.applySurfaces();
  });
  document.addEventListener('dd:variants-full', function () {
    api.applySurfaces();
  });
})();
